import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * The one number the dossier cannot know: how tall the app's own header is.
 *
 * THIS HOOK USED TO MEASURE THREE BANDS AND NOW MEASURES ONE, because the page stopped being
 * bands. When a masthead and a narrator were pinned above a `calc(100vh - …)` column, every one
 * of them had to be measured or the column's pinned footer fell exactly that many pixels below
 * the fold — the failure this file was written for. The dossier is now two panes that each own
 * their own scroll, and the identity block is inside the left one, so nothing above them needs
 * counting. What is left is the app header: 64px on a wide screen, taller below `md` where it
 * grows a second row of navigation, and the sole term in the panes' `height: calc(100vh - …)`.
 *
 * It is found by structure (`main`'s previous sibling in the dashboard layout) rather than by a
 * shared id, so this hook owns the whole mechanism and no other file has to know about it; a
 * layout that does not match keeps the 64px default.
 */
export function useDossierChrome<Root extends HTMLElement>() {
    const rootRef = useRef<Root | null>(null);

    const write = useCallback((name: string, px: number) => {
        rootRef.current?.style.setProperty(name, `${Math.round(px)}px`);
    }, []);

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root || typeof ResizeObserver === 'undefined') return;

        const appHeader = root.closest('main')?.previousElementSibling as HTMLElement | null;
        if (!appHeader) return;

        const measure = () => write('--sd-chrome', appHeader.getBoundingClientRect().height);

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(appHeader);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [write]);

    return { rootRef };
}

/**
 * The scroller a jump target actually lives in, or nothing.
 *
 * THE PAGE IS NOT THE SCROLLER ANY MORE. Above 1024px the canvas is its own scrollport and the
 * document does not scroll at all, so a `window.scrollTo` computed against `window.scrollY`
 * moves nothing and the row jumped to stays exactly where it was. Below 1024px the panes
 * collapse back into ordinary page flow and the document IS the scroller. Rather than encode
 * that breakpoint twice, the target names its own scroller by walking up to the first ancestor
 * that both allows overflow and actually has some.
 */
function scrollportOf(target: HTMLElement): HTMLElement | null {
    for (let node = target.parentElement; node; node = node.parentElement) {
        const overflowY = getComputedStyle(node).overflowY;
        if (
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
            node.scrollHeight > node.clientHeight + 1
        ) {
            return node;
        }
    }
    return null;
}

/**
 * Put the caret where the row said the work is.
 *
 * The block and the field are ids the manifest row carries (`jump.block`, `jump.focus`), and
 * both are allowed not to exist: a canvas that has not been rebuilt yet simply scrolls to the
 * top of the area instead of throwing. Inside a pane the target only has to clear the pane's own
 * top edge; on the page below 1024px it has to clear the app header as well, which is the one
 * thing still pinned over the flow.
 */
export function focusJumpTarget(root: HTMLElement | null, block: string, focus?: string) {
    if (typeof window === 'undefined') return;

    const attempt = (): boolean => {
        const target = document.getElementById(block);
        if (!target) return false;

        const scrollport = scrollportOf(target);
        if (scrollport) {
            const top =
                target.getBoundingClientRect().top -
                scrollport.getBoundingClientRect().top +
                scrollport.scrollTop -
                16;
            scrollport.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        } else {
            const styles = root ? getComputedStyle(root) : null;
            const chrome = parseFloat(styles?.getPropertyValue('--sd-chrome') || '64') || 64;
            const top = target.getBoundingClientRect().top + window.scrollY - chrome - 16;
            window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        }

        const field = focus ? document.getElementById(focus) : null;
        if (field instanceof HTMLElement) field.focus({ preventScroll: true });
        return true;
    };

    // A jump that changes area is a route change: the block being jumped to is mounted by the
    // render that follows, so one frame is not always enough. Two attempts, then it gives up
    // silently — landing at the top of the right area is a fine outcome, an exception is not.
    window.requestAnimationFrame(() => {
        if (attempt()) return;
        window.setTimeout(attempt, 120);
    });
}
